import { useEffect, useRef, useState } from 'react';
import { IcTarget, IcCheck, IcChevron } from './icons.js';
import { splitObjectives, type ScenarioMission } from './scenarioTypes.js';
import './scenario.css';

interface MissionTrackerProps {
  mission: ScenarioMission;
  /** Ids de objetivos cumplidos (optimista: marca y no desmarca). */
  completed: ReadonlyArray<string>;
  /** Id recién marcado este turno → dispara flash + pulse + toast. */
  justId: string | null;
  /**
   * Charla asistida: practicados con la respuesta modelo. Se ven aparte (pip y
   * fila punteados, «CON AYUDA») y NO suman al contador: la misión cuenta sólo
   * lo dicho sin ayuda (Emmanuel, 13/10; mostrarlos, 14/10).
   */
  practiced?: ReadonlyArray<string>;
  /** Id recién practicado con ayuda → aviso de práctica (no de objetivo cumplido). */
  justPracticedId?: string | null;
}

/**
 * Tracker de misión en vivo: overlay glanceable sobre la conversación. Plegado
 * por defecto (barra con pips + contador), se expande al tocar. Optimista —
 * marca objetivos y nunca desmarca. El debrief al cierre es la fuente de
 * verdad; esto es feedback inmediato voice-first.
 *
 * El cierre de la escena vive en el `MissionFinishFab` (botón flotante), no
 * acá: este overlay es sólo visualización del progreso. Ver docs/speak-plan/12.
 */
export function MissionTracker({
  mission,
  completed,
  justId,
  practiced = [],
  justPracticedId = null,
}: MissionTrackerProps) {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<{
    label: string;
    bonus: boolean;
    practiced?: boolean;
  } | null>(null);
  const [pulse, setPulse] = useState(false);
  const [flashId, setFlashId] = useState<string | null>(null);

  const { primary, bonus } = splitObjectives(mission.objectives);
  const allObjs = bonus ? [...primary, bonus] : [...primary];
  const doneSet = new Set(completed);
  const practicedSet = new Set(practiced.filter((id) => !doneSet.has(id)));
  const mainDone = primary.filter((o) => doneSet.has(o.id)).length;
  const mainPracticed = primary.filter((o) => practicedSet.has(o.id)).length;
  const total = primary.length;

  // Reacción a un objetivo recién marcado: flash del pip + pulse del contador + toast.
  const lastJustRef = useRef<string | null>(null);
  useEffect(() => {
    if (!justId || justId === lastJustRef.current) return;
    lastJustRef.current = justId;
    const obj = allObjs.find((o) => o.id === justId);
    if (!obj) return;
    setFlashId(justId);
    setPulse(true);
    setToast({ label: obj.label, bonus: obj.kind === 'bonus' });
    const t1 = setTimeout(() => setPulse(false), 650);
    const t2 = setTimeout(() => setFlashId(null), 700);
    const t3 = setTimeout(() => setToast(null), 2500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
    // allObjs deriva de mission (estable durante la sesión); justId es el único trigger real.
  }, [justId]);

  // Practicado con ayuda: el mismo aviso, dicho como práctica (sin pulse del
  // contador). Los objetivos se leen por ref: el id es el único trigger.
  const objsRef = useRef(allObjs);
  objsRef.current = allObjs;
  const doneRef = useRef(doneSet);
  doneRef.current = doneSet;
  const lastPracticedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!justPracticedId || justPracticedId === lastPracticedRef.current) return;
    lastPracticedRef.current = justPracticedId;
    const obj = objsRef.current.find((o) => o.id === justPracticedId);
    if (!obj || doneRef.current.has(obj.id)) return;
    setToast({ label: obj.label, bonus: false, practiced: true });
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [justPracticedId]);

  return (
    <div className={`mt-wrap${open ? ' open' : ''}`}>
      <button
        type="button"
        className="mt-bar"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Misión"
      >
        <span className="mt-bar-ic">
          <IcTarget size={15} />
        </span>
        <span className="mt-bar-label">Misión</span>
        <div className="mt-pips" aria-hidden="true">
          {allObjs.map((o) => (
            <span
              key={o.id}
              className={`mt-pip${doneSet.has(o.id) ? ' done' : practicedSet.has(o.id) ? ' practiced' : ''}${o.kind === 'bonus' ? ' bonus' : ''}${flashId === o.id ? ' flash' : ''}`}
            />
          ))}
        </div>
        <span className={`mt-count${pulse ? ' pulse' : ''}`}>
          <b>{mainDone}</b>/{total}
        </span>
        {mainPracticed > 0 && <span className="mt-practiced">{mainPracticed} con ayuda</span>}
        <span className="mt-chev">
          <IcChevron size={16} />
        </span>
      </button>

      {toast && (
        <div className="mt-toast">
          <span className="mt-toast-ic">
            <IcCheck size={12} />
          </span>
          <span className="mt-toast-tx">
            {toast.practiced ? (
              <b>Practicado con ayuda: </b>
            ) : toast.bonus ? (
              <b>¡Bonus! </b>
            ) : (
              <b>¡Objetivo! </b>
            )}
            {toast.label}
          </span>
        </div>
      )}

      {open && (
        <div className="mt-panel">
          <div className="mt-panel-title">
            <IcTarget size={14} />
            <span className="t">Tu misión</span>
            <span style={{ flex: 1 }} />
            <span className="sc">
              {mainDone} de {total}
            </span>
          </div>
          {allObjs.map((o) => {
            const done = doneSet.has(o.id);
            const withHelp = practicedSet.has(o.id);
            return (
              <div
                key={o.id}
                className={`mt-item${done ? ' done' : withHelp ? ' practiced' : ''}${o.kind === 'bonus' ? ' bonus' : ''}${flashId === o.id ? ' just' : ''}`}
              >
                <span className="mt-check">{done && <IcCheck size={13} />}</span>
                <span className="mt-label">
                  {o.label}
                  {o.kind === 'bonus' && <span className="mt-bonus-tag">BONUS</span>}
                  {withHelp && <span className="mt-help-tag">CON AYUDA</span>}
                </span>
              </div>
            );
          })}
          {/* Emmanuel (20/10): «con ayuda» no suma a la misión, y tiene que quedar
              claro que se vuelve a pedir, solo, y que así se cumple. */}
          {practicedSet.size > 0 && (
            <p className="mt-help-note">
              Lo que hacés con ayuda es práctica y no cumple la misión. Al terminar te pedimos hacer
              la escena solo: así se cumple.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
